// frontend/src/pages/MarksPage.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Select, Table, InputNumber, Button, Spin, Typography, Space, List, Card, App as AntApp, FloatButton, Alert } from 'antd';
import { useAuth } from '../context/AuthContext';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getAllExams } from '../services/examService';
import { getAllSubjects } from '../services/subjectService';
import { getAllStudents } from '../services/studentService';
import { getClassAssignments, getClassSubjects } from '../services/assignmentService';
import { getMarks, saveMarks } from '../services/markService';
import { getAllClasses } from '../services/classService';
import api from '../services/api';
import useResponsive from '../hooks/useResponsive';
import { SaveOutlined } from '@ant-design/icons'; // Import SaveOutlined for the button

const { Title, Text } = Typography;
const { Option } = Select;

const MarksPage = () => {
    const { message: messageApi } = AntApp.useApp();
    const { user } = useAuth();
    const { currentYear } = useAcademicYear();
    const { isMobile } = useResponsive();

    const [exams, setExams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [allClasses, setAllClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [allAssignments, setAllAssignments] = useState([]);
    const [classSubjectIds, setClassSubjectIds] = useState([]);
    const [marks, setMarks] = useState({});
    const [totalMarks, setTotalMarks] = useState(100);

    const [selectedExam, setSelectedExam] = useState(undefined);
    const [selectedClass, setSelectedClass] = useState(undefined);
    const [selectedSubject, setSelectedSubject] = useState(undefined);

    const [loading, setLoading] = useState({ masters: true, marks: false });
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data);
            } catch (err) {
                console.error("Failed to load settings in MarksPage:", err);
            }
        };
        fetchSettings();
    }, []);

    const isTeacherAssigned = useMemo(() => {
        if (!user || user.role !== 'teacher') return true;
        if (!selectedClass || !selectedSubject) return false;
        
        const isClassTeacher = allClasses.some(c => c.id === selectedClass && c.class_teacher_id === user.id);
        const isSubjectTeacher = allAssignments.some(a => a.teacher_id === user.id && a.class_id === selectedClass && a.subject_id === selectedSubject);
        
        return isClassTeacher || isSubjectTeacher;
    }, [user, selectedClass, selectedSubject, allClasses, allAssignments]);

    const isViewDisabled = user.role === 'teacher' && settings?.allow_teacher_mark_view === false && (!selectedClass || !selectedSubject || !isTeacherAssigned);
    const isEntryDisabled = user.role === 'teacher' && settings?.allow_teacher_mark_entry === false && (!selectedClass || !selectedSubject || !isTeacherAssigned);

    // Fetch master data: exams, subjects, students, classes
    const fetchMasters = useCallback(async () => {
        setLoading(prev => ({ ...prev, masters: true }));
        try {
            const [examsData, subjectsData, studentsData, classesData] = await Promise.all([
                getAllExams(),
                getAllSubjects(),
                getAllStudents(),
                getAllClasses()
            ]);
            // console.log("Fetched Exams:", examsData);
            // console.log("Fetched Subjects:", subjectsData);
            // console.log("Fetched Students:", studentsData);
            // console.log("Fetched Classes:", classesData);
            setExams(examsData);
            setSubjects(subjectsData);
            setStudents(studentsData.map(s => ({ ...s, key: s.id })));
            setAllClasses(classesData);
        } catch (error) {
            messageApi.error('Failed to load initial data.');
        } finally {
            setLoading(prev => ({ ...prev, masters: false }));
        }
    }, [messageApi]);

    useEffect(() => {
        if (currentYear) {
            fetchMasters();
        }
    }, [currentYear, fetchMasters]);

    useEffect(() => {
        if (loading.masters || !user || user.role !== 'teacher') return;
        const fetchAssignments = async () => {
            const visibleClassIds = [...new Set(students.map(s => s.class_id))];
            const assignmentPromises = visibleClassIds.map(id => getClassAssignments(id));
            const assignmentsByClass = await Promise.all(assignmentPromises);
            setAllAssignments(assignmentsByClass.flat());
        };
        fetchAssignments();
    }, [loading.masters, students, user]);

    // Fetch class subjects for selected class — this is used to filter available subjects in Marks.
    useEffect(() => {
        const loadClassSubjects = async () => {
            if (!selectedClass) {
                setClassSubjectIds([]);
                return;
            }
            try {
                const subjIds = await getClassSubjects(selectedClass);
                setClassSubjectIds(subjIds);
            } catch (err) {
                console.error('Failed to fetch class subjects', err);
                setClassSubjectIds([]);
            }
        };
        loadClassSubjects();
    }, [selectedClass]);

    useEffect(() => {
        const fetchMarks = async () => {
            if (!selectedExam || !selectedSubject || !selectedClass) {
                setMarks({});
                setTotalMarks(100);
                return;
            }
            setLoading(prev => ({ ...prev, marks: true }));
            try {
                const marksData = await getMarks(selectedExam, selectedSubject);
                const marksMap = marksData.reduce((acc, mark) => {
                    acc[mark.student_id] = mark.score ? parseFloat(mark.score) : null;
                    return acc;
                }, {});

                if (marksData.length > 0 && marksData[0].total_marks != null) {
                    setTotalMarks(parseFloat(marksData[0].total_marks));
                } else {
                    setTotalMarks(100);
                }

                setMarks(marksMap);

            } catch (error) {
                messageApi.error('Failed to fetch marks.');
            } finally {
                setLoading(prev => ({ ...prev, marks: false }));
            }
        };

        fetchMarks();
    }, [selectedExam, selectedSubject, selectedClass, messageApi]);


    const filteredStudents = useMemo(() => {
        if (!selectedClass) return [];
        return students
            .filter(s => s.class_id === selectedClass)
            .sort((a, b) => {
                const ra = +a.roll_no || 0;
                const rb = +b.roll_no || 0;
                return ra - rb;
            });
    }, [students, selectedClass]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const marksToSave = filteredStudents.map(student => ({
                student_id: student.id,
                score: marks[student.id] || null,
            }));

            await saveMarks(selectedExam, selectedSubject, selectedClass, totalMarks, marksToSave);

            messageApi.success('Marks saved successfully!');
        } catch (error) {
            messageApi.error(error.response?.data?.error || 'Failed to save marks.');
        } finally {
            setSaving(false);
        }
    };


    const handleMarkChange = (studentId, score) => setMarks(prev => ({ ...prev, [studentId]: score }));

    const handleClassChange = (classId) => {
        setSelectedClass(classId);
        setSelectedSubject(undefined);
    };

    const availableClasses = useMemo(() => {
        const value = [...new Map(students.map(item => [item['class_id'], { id: item.class_id, name: item.class_name }])).values()]
            .sort((a, b) => a.name.localeCompare(b.name));
        // console.log("value:", value);
        return value;
    }, [students]);

    const availableSubjects = useMemo(() => {
        if (!selectedClass) return [];
        // Only show subjects that are part of the class's subject list. If none were configured, return [] so the user must configure first.
        if (!classSubjectIds || classSubjectIds.length === 0) return [];
        return subjects.filter(s => classSubjectIds.includes(s.id));
    }, [selectedClass, subjects, classSubjectIds]);


    const columns = [
        // roll number
        { title: 'Roll No', dataIndex: 'roll_no', width: 80, sorter: (a, b) => a.roll_no - b.roll_no },
        { title: 'Admission No', dataIndex: 'admission_no', width: 120, sorter: (a, b) => a.admission_no.localeCompare(b.admission_no) },
        { title: 'Student Name', dataIndex: 'student_name', sorter: (a, b) => a.student_name.localeCompare(b.student_name) },
        {
            title: 'Score',
            key: 'score',
            sorter: (a, b) => {
                const scoreA = marks[a.id] || 0;
                const scoreB = marks[b.id] || 0;
                return scoreA - scoreB;
            },
            render: (_, student) => (
                <Space>
                    <InputNumber
                        min={0}
                        maxLength={(totalMarks ?? 0).toString().length || 3}
                        max={totalMarks}
                        value={marks[student.id]}
                        onChange={(score) => handleMarkChange(student.id, score)}
                        suffix={<Text type="secondary">/ {totalMarks}</Text>}
                        style={{ width: '120px' }}
                        disabled={isEntryDisabled || isViewDisabled}
                    />
                </Space>
            ),
        },
    ];

    const renderMobileList = () => (
        <List
            loading={loading.marks}
            dataSource={filteredStudents}
            renderItem={item => (
                <List.Item>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500 }}>{item.roll_no} - {item.student_name}</div>
                            <div style={{ color: '#8c8c8c', fontSize: '12px' }}>Adm No: {item.admission_no}</div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            {columns[3].render(null, item)}
                        </div>
                    </div>
                </List.Item>
            )}
        />
    );

    return (
        <div>
            {isMobile && !isEntryDisabled && !isViewDisabled && (
                <FloatButton
                    shape="square"
                    type="primary"
                    hidden={!selectedExam || !selectedSubject || !selectedClass}
                    loading={saving}
                    disabled={!selectedExam || !selectedSubject || !selectedClass}
                    onClick={handleSave}
                    style={{ insetInlineEnd: 24, marginBottom: 30 }}
                    icon={<SaveOutlined />}
                />
            )}
            <Title level={2}>Academic Marks</Title>

            {isViewDisabled && (
                <Alert
                    message="Access Denied"
                    description="You do not have permission to view academic marks. This feature has been disabled by the administrator."
                    type="error"
                    showIcon
                    style={{ borderRadius: 12 }}
                />
            )}

            {!isViewDisabled && isEntryDisabled && (
                <Alert
                    message="Mark Entry Disabled"
                    description="You do not have permission to enter or modify academic marks. This feature has been disabled by the administrator."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 12 }}
                />
            )}

            {!isViewDisabled && (
                <>
                    <Card style={{ marginBottom: 16 }}>
                        <Spin spinning={loading.masters}>
                            {/* --- MOBILE/DESKTOP SELECTION LAYOUT FIX --- */}
                            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }} wrap={!isMobile}>
                                <Select
                                    style={{ width: isMobile ? '100%' : 200, marginBottom: isMobile ? 8 : 0 }}
                                    placeholder="1. Select Exam"
                                    onChange={setSelectedExam}
                                    value={selectedExam}
                                >
                                    {exams.map(e => <Option key={e.id} value={e.id}>{e.name}</Option>)}
                                </Select>
                                <Select
                                    style={{ width: isMobile ? '100%' : 200, marginBottom: isMobile ? 8 : 0 }}
                                    placeholder="2. Select Class"
                                    onChange={handleClassChange}
                                    value={selectedClass}
                                >
                                    {availableClasses.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                                </Select>

                                <Select
                                    style={{ width: isMobile ? '100%' : 200, marginBottom: isMobile ? 8 : 0 }}
                                    placeholder="3. Select Subject"
                                    onChange={setSelectedSubject}
                                    value={selectedSubject}
                                    disabled={!selectedClass}
                                >
                                    {availableSubjects.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                                </Select>

                                {selectedClass && classSubjectIds && classSubjectIds.length === 0 && (
                                    <Alert type="warning" message="No subjects assigned to this class. Configure subjects under Class Subjects (Academic Management)" style={{ width: isMobile ? '100%' : 420 }} />
                                )}

                                <InputNumber
                                    addonBefore="Total Marks"
                                    value={totalMarks}
                                    onChange={setTotalMarks}
                                    disabled={isEntryDisabled || !selectedExam || !selectedSubject || !selectedClass}
                                    style={{ width: isMobile ? '100%' : 200 }}
                                />

                                {/* The Save button should only appear when selections are made, especially on desktop */}
                                {!isMobile && !isEntryDisabled && (
                                    <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        onClick={handleSave}
                                        loading={saving}
                                        disabled={!selectedExam || !selectedSubject || !selectedClass}
                                        style={{ height: '40px', borderRadius: '8px', fontWeight: 600 }}
                                    >
                                        Save Academic Marks
                                    </Button>

                                )}
                            </Space>

                            {/* Save button displayed separately on mobile for better full-width placement */}
                            {isMobile && !isEntryDisabled && (
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={handleSave}
                                    loading={saving}
                                    disabled={!selectedExam || !selectedSubject || !selectedClass}
                                    style={{ marginTop: 16, width: '100%', height: '48px', borderRadius: '8px' }}
                                >
                                    Save Academic Marks
                                </Button>
                            )}
                            {/* --- END FIX --- */}
                        </Spin>
                    </Card>
                    <Card style={{ marginTop: 16 }}>
                        {isMobile ? renderMobileList() : (
                            <Table
                                dataSource={filteredStudents}
                                columns={columns}
                                bordered
                                pagination={false}
                                loading={loading.marks}
                            />
                        )}
                    </Card>
                </>
            )}
        </div>
    );
};

export default MarksPage;